import { AppliedBundle } from '../applied_bundle';
import { RecurringInvoice, RecurringInvoiceBundle, RecurringInvoiceLineItem } from '../recurring_invoice';
import { TaxRate } from '../tax-rate';
import { AppliedTaxRate, Invoice, InvoiceItem, InvoiceStatus } from './invoice';

export function taxRatesFromLineItem(item: RecurringInvoiceLineItem, allTaxRates: TaxRate[]): TaxRate[] {
  const invoiceLineItemTaxRates: TaxRate[] = [];
  item.taxRateIds?.forEach((taxId) => {
    const taxRate = allTaxRates.find((t) => t.id === taxId);
    if (taxRate) {
      invoiceLineItemTaxRates.push({
        id: taxId,
        countryCode: taxRate.countryCode,
        stateCode: taxRate.stateCode,
        name: taxRate.name,
        description: taxRate.description,
        percentage: taxRate.percentage,
      });
    }
  });
  return invoiceLineItemTaxRates;
}

export function invoiceLineItemFromLineItem(
  item: RecurringInvoiceLineItem,
  taxRates: TaxRate[],
  bundleId: string,
): InvoiceItem {
  const invoiceTaxRates = taxRatesFromLineItem(item, taxRates);
  const subtotal = item.quantity * item.unitPrice;

  const appliedRates: AppliedTaxRate[] = [];
  let taxTotal = 0;
  invoiceTaxRates.forEach((t) => {
    if (!t) {
      return;
    }
    const taxAmount = t.percentage ? subtotal * (t.percentage / 100) : 0;
    taxTotal += taxAmount;
    appliedRates.push({
      amount: taxAmount,
      taxRateId: t.id,
      percentage: t.percentage,
      name: t.description ? `${t.name} - ${t.description} (${t.percentage}%)` : `${t.name} (${t.percentage}%)`,
    });
  });

  return {
    sku: item.sku || '',
    amount: item.unitPrice,
    quantity: item.quantity,
    description: item.name,
    subtotal: subtotal,
    total: subtotal + taxTotal,
    appliedTaxes: appliedRates,
    taxRates: invoiceTaxRates,
    bundleId: bundleId,
  } as InvoiceItem;
}

export function invoiceFromRecurringInvoice(recurringInvoice: RecurringInvoice, taxRates: TaxRate[]): Invoice {
  const invoiceLineItems: InvoiceItem[] = [];
  const appliedBundles: AppliedBundle[] = [];
  let subtotal = 0;
  let total = 0;

  let i = 0;
  recurringInvoice.items.forEach((item) => {
    if (item instanceof RecurringInvoiceLineItem) {
      invoiceLineItems.push(invoiceLineItemFromLineItem(item, taxRates, ''));
    } else if (item instanceof RecurringInvoiceBundle) {
      const tempBundleId = String(i);
      i++;
      const bundleInvoiceItems = item.lineItems.map((li) => invoiceLineItemFromLineItem(li, taxRates, tempBundleId));
      appliedBundles.push({
        name: item.name,
        hideBundleItems: item.hideLineItems,
        bundleId: tempBundleId,
      });
      invoiceLineItems.push(...bundleInvoiceItems);
    }
  });

  const taxTotals = new Map<string, AppliedTaxRate>();
  invoiceLineItems.forEach((li) => {
    total += li.total;
    subtotal += li.subtotal;
    (li.appliedTaxes || []).forEach((t) => {
      const tax = { ...t };
      if (!tax.taxRateId) {
        return;
      }
      if (!taxTotals.has(tax.taxRateId)) {
        taxTotals.set(tax.taxRateId, tax);
      } else {
        const taxValue = taxTotals.get(tax.taxRateId) as AppliedTaxRate;
        if (taxValue.amount !== undefined) {
          taxValue.amount += tax?.amount || 0;
        } else {
          taxValue.amount = 0;
        }
        taxTotals.set(tax.taxRateId, taxValue);
      }
    });
  });

  const netD = recurringInvoice.netD ? recurringInvoice.netD : 7;
  const dueDate = new Date(recurringInvoice.nextIssue);
  dueDate.setDate(dueDate.getDate() + netD);

  return {
    customerId: recurringInvoice.customerId,
    status: InvoiceStatus.DRAFT,
    subtotal: subtotal,
    total: total,
    items: invoiceLineItems,
    appliedBundles: appliedBundles,
    appliedTaxes: [...taxTotals.values()],
    currency: recurringInvoice.currencyCode,
    issued: recurringInvoice.nextIssue,
    due: dueDate,
    contactId: recurringInvoice.recipientUserId,
    collectionMethod: recurringInvoice.collectionMethod,
    defaultPaymentMethod: recurringInvoice.paymentProcessorId,
    memo: recurringInvoice.memo,
  } as Invoice;
}
